import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ValeurListeFixeModel } from 'src/app/models/listes-fixes/valeur-liste-fixe.model';
import { IChampPersoViewModel } from 'src/app/pages/champs-perso/models/champ-perso-models';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { DocumentReadModel } from '../../models/documents-service/document-read-model';
import { FormateurModel } from '../../models/formateurs/formateur.model';
import { ResumeFormateurModel } from '../../models/sessions/resume-session-model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class FormateursService {
  private readonly crmApiUrl = `${this.configService.config.urlApi}/api/crm`;
  private readonly ApiUrl = `${this.configService.config.urlApi}/api`;

  constructor(
    private oDataAdaptor: ODataAdaptor,
    private http: HttpClient,
    private notificationToastService: NotificationToastService,
    private uploadUtilService: UploadUtilService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  getFormateurDetaille(idFormateur: number): Observable<FormateurModel> {
    return this.http.get<FormateurModel>(`${this.crmApiUrl}/formateurs/getFormateur/${idFormateur}`).pipe(
      map(f => {
        if (f.dateDeNaissance != null) {
          f.dateDeNaissance = new Date(f.dateDeNaissance);
        }
        return f;
      }),
    );
  }

  public getFormateurs(idSession: number): Observable<ResumeFormateurModel[]> {
    return this.http.get<ResumeFormateurModel[]>(
      `${this.ApiUrl}/f/SessionFormateur/GetFormateurs/${idSession}`,
    );
  }
  // getFormateurs(idsFormateurs: number[]): Observable<ResumeFormateurModel[]> {
  //   return this.http.post<Array<ResumeFormateurModel>>(
  //     `${this.crmApiUrl}/formateurs/getFormateurs`,
  //     idsFormateurs,
  //   );
  // }

  getExportURL(): string {
    return `${this.crmApiUrl}/formateurs/ExporterExcel`;
  }

  buildDataManagerGetFormateurs(): DataManager {
    return new DataManager({
      url: `${this.crmApiUrl}/FormateursView`,
      adaptor: this.oDataAdaptor,
    });
  }

  addFormateur(formateur: FormateurModel): Observable<number> {
    return this.http
      .put<number>(`${this.crmApiUrl}/formateurs/addformateur`, formateur)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le formateur a bien été créé.'))));
  }

  updateFormateur(formateur: FormateurModel): Observable<number> {
    return this.http.post(`${this.crmApiUrl}/formateurs/updateFormateur`, formateur).pipe(
      tap((idFormateur: number) => {
        this.notificationToastService.push(new ToastSuccesModel('Le formateur a bien été modifié.'));
        return idFormateur;
      }),
    );
  }

  activerDesactiverFormateur(formateur: FormateurModel) {
    const statusText = formateur.actif === true ? 'activé' : 'désactivé';
    return this.http
      .post(`${this.crmApiUrl}/formateurs/ToggleActivationFormateur`, {
        id: formateur.id,
        actif: formateur.actif,
      })
      .pipe(
        tap(() => this.notificationToastService.push(new ToastSuccesModel(`Le formateur a été ${statusText}`))),
      );
  }

  televerserPhoto(photo: File, idIndividu: number) {
    return this.uploadUtilService
      .uploadFile(`${this.crmApiUrl}/formateurs/definirPhoto`, {File: photo, idIndividu})
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  supprimerDocument(idDocument: string):Observable<any> {
    return this.http
      .delete(`${this.crmApiUrl}/formateurs/SupprimerDocument/${idDocument}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Document supprimé avec succès`))));
  }

  supprimerPhoto(idFormateur: number): Observable<void> {
    return this.http
      .delete<void>(`${this.crmApiUrl}/formateurs/deletePhoto/${idFormateur}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Photo supprimée avec succès`))));
  }

  televerserDocument(document: File, idFormateur: number, updateFunction: () => any = null): Observable<DocumentReadModel> {
    return this.uploadUtilService
      .uploadFile(`${this.crmApiUrl}/formateurs/AddDocument`, {File: document, idFormateur}, updateFunction)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  buildDataManagerGetDocumentsFormateur(idFormateur) {
    return new DataManager({
      url: `${this.crmApiUrl}/formateurs/GetDocuments/${idFormateur}`,
      adaptor: this.oDataAdaptor,
    });

  }

  buildDataManagerGetEmailsFormateur(idFormateur) {
    return new DataManager({
      url: `${this.crmApiUrl}/formateurs/GetEmails/${idFormateur}`,
      adaptor: this.oDataAdaptor,
    });
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.crmApiUrl}/ListeFixeEtatsFormateur/getValeurs`);
  }

  public getChampsAdditionnels(): Observable<IChampPersoViewModel[]>{
    return this.http.get<IChampPersoViewModel[]>(`${this.crmApiUrl}/ChampsPersoFormateur/GetChamps`)
  }

  public getPersonneForLink(idFormateur: number, recherche?: string):Observable<any>{
    return this.http.get(`${this.crmApiUrl}/PersonnesView?$filter=Statut eq 'Actif'&$orderby=Nom,Prenom&idFormateur=${idFormateur}&recherche=${recherche ? recherche : ''}`);
  }

  public buildDataManagerGetFormations(idFormateur: number): DataManager {
    return new DataManager({
      url: `${this.ApiUrl}/f/FormateurHabilitationsView?idFormateur=${idFormateur}`,
      adaptor: this.oDataAdaptor
    });
  }

  public getFormations(idFormateur: number): Observable<any>{
    return this.http.get(`${this.ApiUrl}/f/FormateurHabilitations/getHabilitableFormations/${idFormateur}`)
  }

  public addFormations(idFormateur: number, idsFormations: number[]): Observable<any>{
    return this.http.post(`${this.ApiUrl}/f/FormateurHabilitations/addFormateurFormation/${idFormateur}`, {idsFormations: idsFormations})
  }

  public deleteFormation(idFormateur: number, idFormation: number){
    return this.http.delete(`${this.ApiUrl}/f/FormateurHabilitations/deleteFormateurFormation/${idFormateur}/${idFormation}`)

  }
}
